import {
  BookMarked,
  BookOpen,
  Briefcase,
  ChartLine,
  CircleHelp,
  Layers,
  Network,
  Target,
  Workflow,
} from "lucide-react"
import { useEffect, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router"

import { CatalogoCargos } from "@/componentes/cargos/CatalogoCargos"
import { CatalogoDepartamentos } from "@/componentes/departamentos/CatalogoDepartamentos"
import { Indicadores } from "@/componentes/estrategia/Indicadores"
import { Aparece } from "@/componentes/ui/Aparece"
import { Pestanas } from "@/componentes/ui/Pestanas"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { Biblioteca } from "@/paginas/interno/Biblioteca"
import { Estrategia } from "@/paginas/interno/Estrategia"
import { Manual } from "@/paginas/interno/Manual"
import { Organigrama } from "@/paginas/interno/Organigrama"
import { Procesos } from "@/paginas/interno/Procesos"

/**
 * En el orden en que se planifica: hacia dónde va la agencia, cómo se organiza para
 * llegar —el mapa, sus áreas y sus puestos—, cómo trabaja, qué documentos lo rigen y
 * el manual que lo reúne todo.
 */
const VISTAS = [
  { valor: "estrategia", etiqueta: "Estrategia", icono: Target, permiso: "estrategia:ver" },
  { valor: "indicadores", etiqueta: "Indicadores", icono: ChartLine, permiso: "indicador:ver" },
  { valor: "organigrama", etiqueta: "Organigrama", icono: Network, permiso: "organigrama:ver" },
  {
    valor: "departamentos",
    etiqueta: "Departamentos",
    icono: Layers,
    permiso: "departamento:ver",
  },
  { valor: "cargos", etiqueta: "Cargos", icono: Briefcase, permiso: "cargo:ver" },
  { valor: "procesos", etiqueta: "Procesos", icono: Workflow, permiso: "proceso:ver" },
  { valor: "biblioteca", etiqueta: "Biblioteca SGI", icono: BookOpen, permiso: "biblioteca:ver" },
  { valor: "manual", etiqueta: "Manual", icono: BookMarked, permiso: "manual:ver" },
] as const

export type VistaPlanificacion = (typeof VISTAS)[number]["valor"]

/** La frase bajo el título, la de la pestaña que se está mirando. */
const SUBTITULO: Record<VistaPlanificacion, string> = {
  estrategia:
    "La visión y la misión, desplegadas en pilares, objetivos medibles y hitos con fecha.",
  indicadores: "Lo que mide cada departamento, contra su meta, mes a mes y por trimestre.",
  organigrama: "Cada caja es una posición: su cargo, y quien la ocupa si está cubierta.",
  departamentos: "Las áreas de la agencia y los puestos que cuelgan de cada una.",
  cargos: "Qué puestos hay, en qué área está cada uno y qué se espera de él.",
  procesos: "Las funciones que describas aquí arman solas el descriptivo de cada cargo.",
  biblioteca:
    "Códigos, manuales, planes, políticas y procedimientos del Sistema de Gestión Integral.",
  manual:
    "Se arma solo con la identidad, el despliegue estratégico, los organigramas y los procesos.",
}

/** El «?»: las reglas de cada vista que la pantalla no dice sola. */
const AYUDA: Record<VistaPlanificacion, readonly string[]> = {
  indicadores: [
    "Cumplimiento: valor ÷ meta si más es mejor; meta ÷ valor si menos es mejor.",
    "Verde desde el 100 %; amarillo dentro de la tolerancia; rojo es desvío. El trimestre cierra con su última medición.",
  ],
  estrategia: [
    "El avance de cada objetivo sale de sus etapas completadas: nadie lo teclea.",
    "Un objetivo sin etapas avanza 0, no 100; y un pilar sin objetivos no cuenta para el avance de la agencia.",
  ],
  organigrama: [
    "Una caja es una posición, no una persona: la vacante es una posición sin nadie, y una persona puede ocupar varias.",
    "El jefe directo es la línea sólida; los demás jefes, punteadas.",
  ],
  departamentos: [
    "Es cada cargo el que dice a qué área pertenece: un puesto se cambia de área al editarlo en «Cargos».",
    "Un departamento se archiva, no se borra. Sus cargos siguen en él —es lo que explica dónde trabajaba su gente—, pero deja de ofrecerse al elegir un área.",
  ],
  cargos: [
    "El nombre es uno solo para todas las fichas, contratos y organigramas: cambiarlo aquí los cambia a todos.",
    "Las funciones escritas a mano salen en el contrato, en este orden; las del flujo se arman solas con los pasos de «Procesos».",
    "Un cargo no se borra: se retira. Y con gente dentro, ni eso.",
  ],
  procesos: [
    "Cada paso dice qué cargo responde por él.",
    "El descriptivo de cada cargo se arma solo con los pasos que tiene asignados: no se escribe aparte.",
  ],
  biblioteca: [
    "Redactar y aprobar son permisos distintos: quien escribe el procedimiento no es necesariamente quien responde de que se siga.",
    "Una versión publicada congela lo que decía, y un documento con versiones no se borra: se marca obsoleto.",
  ],
  manual: [
    "No se redacta: se arma al pedirlo con la identidad, la estrategia, los organigramas y los procesos.",
    "Una edición definitiva congela lo de ese día; el borrador dice siempre la verdad de hoy.",
  ],
}

/**
 * Planificación estratégica: UNA entrada del menú con sus siete vistas en pestañas,
 * como Tesorería.
 *
 * ⚠ **Fueron cinco entradas** —organigrama, estrategia, biblioteca, manual y
 * procesos— y el menú crecía una línea por cada vista de lo mismo. La vista va en la
 * URL (`/planificacion/estrategia`); las direcciones viejas —`/organigrama`,
 * `/biblioteca/7`…— llevan a su pestaña (`RutaVieja`, `DocumentoViejo`). Los cargos
 * eran además una pestaña de «Personas», y los departamentos no tenían pantalla.
 *
 * ⚠ **Cada pestaña pide su propio permiso, y sólo salen las que se pueden abrir.**
 * `planificacion:ver` ofrece la entrada; ver el organigrama y leer la biblioteca
 * siguen siendo decisiones distintas (ver `cli.py::PERMISOS`). Una pestaña que se
 * enseñara para responder 403 al abrirla es peor que no enseñarla.
 *
 * ⚠ **La cabecera y las pestañas no se imprimen.** El organigrama y el manual se
 * exportan con la impresión del navegador; la hoja lleva el documento, no la pantalla.
 *
 * ⚠ **Se monta sólo la pestaña activa**, con `replace` al cambiar, igual que en
 * Tesorería: sin pagar las consultas de lo que no se ve, y sin llenar el historial.
 */
export function Planificacion() {
  const { vista: enRuta } = useParams()
  const navegar = useNavigate()
  const usuario = usarSesion((e) => e.usuario)
  const [ayuda, setAyuda] = useState(false)

  const visibles = VISTAS.filter((v) => tienePermiso(usuario, v.permiso))
  // Derivada: una vista que no existe o que no se puede abrir cae a la primera que sí.
  const vista = visibles.find((v) => v.valor === enRuta) ?? visibles[0] ?? null

  useEffect(() => {
    if (vista !== null && enRuta !== undefined && enRuta !== vista.valor) {
      navegar(`/planificacion/${vista.valor}`, { replace: true })
    }
  }, [enRuta, vista, navegar])

  return (
    <div>
      <header className="no-print mb-4">
        <div className="flex items-center gap-1">
          <h1 className="text-xl font-semibold">Planificación estratégica</h1>
          {vista !== null && (
            <button
              type="button"
              aria-expanded={ayuda}
              aria-controls={ayuda ? "ayuda-planificacion" : undefined}
              onClick={() => setAyuda((a) => !a)}
              className="foco-cristal toque-cristal inline-flex size-7 items-center justify-center rounded-[var(--radius-control)]"
              style={{ color: "var(--fg-muted)" }}
            >
              <CircleHelp size={16} aria-hidden={true} />
              <span className="sr-only">Cómo funciona esta pestaña</span>
            </button>
          )}
        </div>
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {vista === null
            ? "Hacia dónde va la agencia, cómo se organiza y cómo trabaja."
            : SUBTITULO[vista.valor]}
        </p>
        {vista !== null && (
          <Aparece visible={ayuda} className="pb-1">
            <div id="ayuda-planificacion" className="tarjeta mt-3 max-w-2xl">
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {AYUDA[vista.valor].map((linea) => (
                  <li key={linea}>{linea}</li>
                ))}
              </ul>
            </div>
          </Aparece>
        )}
      </header>

      {vista === null ? (
        <p className="tarjeta text-sm" style={{ color: "var(--fg-muted)" }}>
          No tienes acceso a ninguna de sus vistas. Cada una pide su propio permiso, y se conceden
          desde «Roles y permisos».
        </p>
      ) : (
        <>
          <div className="no-print">
            <Pestanas
              pestanas={visibles}
              activa={vista.valor}
              onCambiar={(v) => navegar(`/planificacion/${v}`, { replace: true })}
              etiqueta="Vistas de planificación estratégica"
              idPanel="panel-planificacion"
              prefijoId="pestana-planificacion"
            />
          </div>
          <div
            id="panel-planificacion"
            role="tabpanel"
            aria-labelledby={`pestana-planificacion-${vista.valor}`}
          >
            {vista.valor === "estrategia" && <Estrategia />}
            {vista.valor === "indicadores" && <Indicadores />}
            {vista.valor === "organigrama" && <Organigrama />}
            {vista.valor === "departamentos" && <CatalogoDepartamentos />}
            {vista.valor === "cargos" && <CatalogoCargos />}
            {vista.valor === "procesos" && <Procesos />}
            {vista.valor === "biblioteca" && <Biblioteca />}
            {vista.valor === "manual" && <Manual />}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * La dirección de cuando la vista era su propia entrada del menú. Hay enlaces
 * guardados y marcadores: tienen que seguir llevando a algún sitio.
 */
export function RutaVieja({ vista }: { vista: VistaPlanificacion }) {
  return <Navigate to={`/planificacion/${vista}`} replace={true} />
}

/** `/biblioteca/7`, de antes: el documento, en su sitio nuevo. */
export function DocumentoViejo() {
  const { id } = useParams()
  return <Navigate to={`/planificacion/biblioteca/${id ?? ""}`} replace={true} />
}
